'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  type ThemePreference,
  readThemePreference,
  persistThemePreference,
  applyThemeToDocument,
  resolveDarkActive,
  toggleLightDarkPreference,
  ensureThemeOnDocument,
} from '@/lib/theme';

type ThemeContextValue = {
  themePref: ThemePreference;
  resolvedDark: boolean;
  setThemePreference: (pref: ThemePreference) => void;
  cycleThemePreference: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePref, setThemePref] = useState<ThemePreference>(() =>
    typeof window === 'undefined' ? 'light' : readThemePreference()
  );
  const [resolvedDark, setResolvedDark] = useState(() =>
    typeof window === 'undefined' ? false : resolveDarkActive(readThemePreference())
  );

  const syncResolved = useCallback((pref: ThemePreference) => {
    applyThemeToDocument(pref);
    setResolvedDark(resolveDarkActive(pref));
  }, []);

  /** Antes da pintura — evita flash e corrige hidratação do Chrome no `<html>`. */
  useLayoutEffect(() => {
    const pref = readThemePreference();
    setThemePref(pref);
    syncResolved(pref);
  }, [syncResolved]);

  useLayoutEffect(() => {
    applyThemeToDocument(themePref);
    setResolvedDark(resolveDarkActive(themePref));
  }, [themePref]);

  /**
   * Chrome (via React/Next) pode sobrescrever `class` do `<html>` após o toggle.
   * Observador leve restaura `dark`/`light` sem loop (applyThemeToDocument faz no-op se ok).
   */
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      ensureThemeOnDocument(themePref);
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
    return () => observer.disconnect();
  }, [themePref]);

  useEffect(() => {
    if (themePref !== 'system' || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => syncResolved('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [themePref, syncResolved]);

  const setThemePreference = useCallback(
    (pref: ThemePreference) => {
      persistThemePreference(pref);
      setThemePref(pref);
      syncResolved(pref);
    },
    [syncResolved]
  );

  const cycleThemePreference = useCallback(() => {
    const next = toggleLightDarkPreference(themePref);
    setThemePreference(next);
  }, [themePref, setThemePreference]);

  const value = useMemo(
    () => ({
      themePref,
      resolvedDark,
      setThemePreference,
      cycleThemePreference,
    }),
    [themePref, resolvedDark, setThemePreference, cycleThemePreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme deve ser usado dentro de ThemeProvider');
  }
  return ctx;
}
